import type { ReactNode, SVGProps } from "react";

/**
 * TES-1086 — Selbst entworfene Landing-Illustrationen im Industrial-DS-v2-Stil
 * (Blueprint-/Schaltplan-Ästhetik) als Ersatz für generische Stock-Cliparts.
 *
 * Stilsprache:
 * - Deep-Blue-Linien (#00234d, Brand) auf Surface (#f7f9fb), Safety-Yellow-
 *   Akzente (#fed400) — bewusst LITERALE Farben: die Kompositionen sind wie
 *   Bild-Assets modus-invariant (Light + Dark identisch, AC TES-1086).
 * - Einheitliche Strichstärke 4 (bei 256er-ViewBox), runde Caps/Joins.
 * - Technisches Raster + Registrierungs-Ecken wie der Hero-Grid-Backdrop.
 */

const NAVY = "#00234d"; // --brand (mode-invariant)
const YELLOW = "#fed400"; // --secondary Safety Yellow (mode-invariant)
const SURFACE = "#f7f9fb"; // --surface light
const PAPER = "#ffffff";
const STROKE = 4;

interface IllustrationProps extends Omit<SVGProps<SVGSVGElement>, "id"> {
  /** Zugänglicher Bild-Titel (ersetzt das alt-Attribut der PNG-Vorgänger). */
  title: string;
}

interface FrameProps extends IllustrationProps {
  /** Eindeutiger Präfix für pattern-/clipPath-IDs (mehrere SVGs pro Seite!). */
  idPrefix: string;
  children: ReactNode;
}

/** Gemeinsame Blueprint-Kachel: Surface-Grund, Technik-Raster, Eck-Marken. */
function BlueprintFrame({ idPrefix, title, children, ...svgProps }: FrameProps) {
  const gridId = `${idPrefix}-grid`;
  const clipId = `${idPrefix}-clip`;
  return (
    <svg viewBox="0 0 256 256" role="img" aria-label={title} {...svgProps}>
      <title>{title}</title>
      <defs>
        <pattern id={gridId} width="32" height="32" patternUnits="userSpaceOnUse">
          <path
            d="M 32 0 L 0 0 0 32"
            fill="none"
            stroke={NAVY}
            strokeOpacity="0.08"
            strokeWidth="1"
          />
        </pattern>
        <clipPath id={clipId}>
          <rect width="256" height="256" rx="20" />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        <rect width="256" height="256" rx="20" fill={SURFACE} />
        <rect width="256" height="256" fill={`url(#${gridId})`} />
      </g>
      <rect
        x="1"
        y="1"
        width="254"
        height="254"
        rx="19"
        fill="none"
        stroke={NAVY}
        strokeOpacity="0.15"
        strokeWidth="2"
      />
      {/* Registrierungs-Ecken (technische Zeichnung) */}
      <g fill="none" stroke={NAVY} strokeOpacity="0.4" strokeWidth="2" strokeLinecap="round">
        <path d="M 20 30 V 20 H 30" />
        <path d="M 226 20 H 236 V 30" />
        <path d="M 236 226 V 236 H 226" />
        <path d="M 30 236 H 20 V 226" />
      </g>
      <g
        fill="none"
        stroke={NAVY}
        strokeWidth={STROKE}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {children}
      </g>
    </svg>
  );
}

/** Schritt 1 — Checklisten-Editor: Dokument mit Prüfpunkten, Stift, Feld-Hinzufügen. */
export function ChecklistEditorIllustration({ title, ...props }: IllustrationProps) {
  return (
    <BlueprintFrame idPrefix="bp-checklist" title={title} {...props}>
      {/* Dokument */}
      <rect x="68" y="44" width="120" height="168" rx="10" fill={PAPER} />
      {/* Titelzeile */}
      <line x1="88" y1="72" x2="152" y2="72" />
      {/* Prüfpunkt 1 — bestanden (Safety-Yellow-Akzent) */}
      <rect x="88" y="96" width="20" height="20" rx="4" fill={YELLOW} />
      <path d="M 93 106 l 5 5 l 9 -11" />
      <line x1="122" y1="106" x2="168" y2="106" />
      {/* Prüfpunkt 2 — bestanden */}
      <rect x="88" y="132" width="20" height="20" rx="4" fill={PAPER} />
      <path d="M 93 142 l 5 5 l 9 -11" />
      <line x1="122" y1="142" x2="160" y2="142" />
      {/* Prüfpunkt 3 — offen */}
      <rect x="88" y="168" width="20" height="20" rx="4" fill={PAPER} />
      <line x1="122" y1="178" x2="164" y2="178" />
      {/* Feld hinzufügen (Plus-Chip) */}
      <circle cx="196" cy="64" r="16" fill={YELLOW} />
      <path d="M 196 56 v 16 M 188 64 h 16" />
      {/* Stift (zeigt auf den offenen Prüfpunkt) */}
      <g transform="rotate(-45 176 192)">
        <rect x="169" y="160" width="14" height="44" rx="3" fill={YELLOW} />
        <path d="M 169 204 L 176 218 L 183 204 Z" fill={PAPER} />
      </g>
    </BlueprintFrame>
  );
}

/** Schritt 2 — Mobile Prüfung vor Ort: Smartphone-Checkliste, Standort-Pin, Konnektivität. */
export function MobileInspectionIllustration({ title, ...props }: IllustrationProps) {
  return (
    <BlueprintFrame idPrefix="bp-mobile" title={title} {...props}>
      {/* Standlinie (vor Ort) */}
      <line
        x1="48"
        y1="206"
        x2="208"
        y2="206"
        strokeWidth="2"
        strokeOpacity="0.35"
        strokeDasharray="8 8"
      />
      {/* Smartphone */}
      <rect x="96" y="40" width="64" height="148" rx="12" fill={PAPER} />
      <line x1="120" y1="56" x2="136" y2="56" />
      {/* Checkliste auf dem Screen */}
      <rect x="108" y="72" width="16" height="16" rx="3" fill={YELLOW} strokeWidth="3" />
      <path d="M 112 80 l 4 4 l 7 -9" strokeWidth="3" />
      <line x1="132" y1="80" x2="148" y2="80" strokeWidth="3" />
      <rect x="108" y="100" width="16" height="16" rx="3" fill={PAPER} strokeWidth="3" />
      <line x1="132" y1="108" x2="144" y2="108" strokeWidth="3" />
      {/* Fortschrittsbalken */}
      <rect x="108" y="132" width="40" height="10" rx="5" fill={PAPER} strokeWidth="3" />
      <rect x="111" y="135" width="20" height="4" rx="2" fill={YELLOW} stroke="none" />
      {/* Home-Indikator */}
      <line x1="120" y1="172" x2="136" y2="172" />
      {/* Standort-Pin */}
      <path
        d="M 64 124 c -11 0 -19 8 -19 18 c 0 13 19 36 19 36 c 0 0 19 -23 19 -36 c 0 -10 -8 -18 -19 -18 Z"
        fill={PAPER}
      />
      <circle cx="64" cy="143" r="6" fill={YELLOW} strokeWidth="3" />
      {/* Konnektivität (online/offline) */}
      <circle cx="190" cy="86" r="3" fill={NAVY} stroke="none" />
      <path d="M 190 70 a 16 16 0 0 1 16 16" />
      <path d="M 190 52 a 34 34 0 0 1 34 34" />
    </BlueprintFrame>
  );
}

/** Schritt 3 — PDF-Bericht: Ergebnis-Dokument mit Eselsohr, Messbalken, PDF-Badge. */
export function PdfReportIllustration({ title, ...props }: IllustrationProps) {
  return (
    <BlueprintFrame idPrefix="bp-report" title={title} {...props}>
      {/* Generierungs-Impuls (Automatik) */}
      <g strokeWidth="3" strokeOpacity="0.35">
        <line x1="52" y1="72" x2="64" y2="72" />
        <line x1="44" y1="92" x2="64" y2="92" />
        <line x1="52" y1="112" x2="64" y2="112" />
      </g>
      {/* Dokument mit Eselsohr */}
      <path
        d="M 76 50 a 10 10 0 0 1 10 -10 H 156 L 188 72 V 182 a 10 10 0 0 1 -10 10 H 86 a 10 10 0 0 1 -10 -10 Z"
        fill={PAPER}
      />
      <path d="M 156 40 V 72 H 188 Z" fill={YELLOW} />
      {/* Ergebnis-Diagramm */}
      <rect x="100" y="112" width="14" height="28" fill={PAPER} />
      <rect x="124" y="96" width="14" height="44" fill={YELLOW} />
      <rect x="148" y="120" width="14" height="20" fill={PAPER} />
      <line x1="92" y1="140" x2="172" y2="140" />
      {/* Textzeilen */}
      <line x1="92" y1="158" x2="172" y2="158" strokeWidth="3" />
      <line x1="92" y1="172" x2="148" y2="172" strokeWidth="3" />
      {/* PDF-Badge */}
      <rect x="52" y="160" width="52" height="30" rx="6" fill={NAVY} />
      <text
        x="78"
        y="181"
        textAnchor="middle"
        fill={PAPER}
        stroke="none"
        fontFamily="inherit"
        fontSize="16"
        fontWeight="700"
        letterSpacing="1"
      >
        PDF
      </text>
    </BlueprintFrame>
  );
}

/** Trust — Sicherheitsschild: Schild mit Schloss und Schaltplan-Anschlüssen. */
export function SecurityShieldIllustration({ title, ...props }: IllustrationProps) {
  return (
    <BlueprintFrame idPrefix="bp-shield" title={title} {...props}>
      {/* Schaltplan-Anschlüsse */}
      <path d="M 72 100 H 56 V 84 H 48" />
      <circle cx="42" cy="84" r="5" fill={YELLOW} strokeWidth="3" />
      <path d="M 72 132 H 48" />
      <circle cx="42" cy="132" r="5" fill={YELLOW} strokeWidth="3" />
      <path d="M 184 100 H 200 V 120 H 208" />
      <circle cx="214" cy="120" r="5" fill={YELLOW} strokeWidth="3" />
      <path d="M 184 84 H 208" />
      <circle cx="214" cy="84" r="5" fill={YELLOW} strokeWidth="3" />
      {/* Schild */}
      <path
        d="M 128 40 L 184 60 V 116 C 184 156 161 187 128 204 C 95 187 72 156 72 116 V 60 Z"
        fill={PAPER}
      />
      {/* Schloss */}
      <path d="M 114 116 V 102 a 14 14 0 0 1 28 0 V 116" fill="none" />
      <rect x="106" y="116" width="44" height="36" rx="6" fill={YELLOW} />
      <circle cx="128" cy="130" r="4" fill={NAVY} stroke="none" />
      <line x1="128" y1="134" x2="128" y2="142" />
    </BlueprintFrame>
  );
}
